<template>
  <div
    ref="root"
    class="search-results"
  >
    <div
      v-if="isLoading || !isFirstRequestDone"
      class="search-results__skeletons"
    >
      <div
        v-for="index in 9"
        :key="index"
        class="d-flex py-4 mx-2"
        :class="xSpaceClass"
      >
        <v-skeleton-loader
          type="avatar"
        />
        <v-skeleton-loader
          class="ml-5"
          type="text"
          width="96%"
        />
      </div>
    </div>
    <div
      v-else-if="!searchResults.length && (query.length < MIN_QUERY_LENGTH)"
      class="py-4"
    >
      <NavLinks
        :town="town"
        :x-space-class="xSpaceClass"
      />
    </div>
    <template v-else>
      <div
        v-for="searchResult in searchResultsWithIndexes"
        :key="searchResult.title"
      >
        <div
          class="text-body-2 text-secondary--text pt-4 pb-2"
          :class="xSpaceClass"
        >
          {{ searchResult.title }}
        </div>
        <div>
          <a
            v-for="searchResultItem in searchResult.results"
            :key="searchResultItem.link"
            :href="searchResultItem.link"
            class="search-results__result-item"
            :class="[
              {
                'search-results__result-item_active': searchResultItem.index === activeItemIndex,
              },
              xSpaceClass,
            ]"
            @click="handleClickSearchResult"
          >
            <div class="search-results__image-wrapper">
              <img
                v-if="searchResultItem.image"
                class="search-results__image"
                alt="Фото"
                :src="searchResultItem.image"
                width="40"
                height="40"
              >
              <span
                v-else-if="searchResultItem.icon"
                class="icon-secondary--text"
                :class="ICON_MAP[searchResultItem.icon]"
              />
            </div>
            <div class="ml-4">
              <div
                class="text-body-1 text--text"
                v-html="highlightQuery(searchResultItem.title)/* eslint-disable-line vue/no-v-html */"
              />
              <div class="text-body-2 text-secondary--text">
                {{ searchResultItem.subtitle }}
              </div>
            </div>
          </a>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue'
import { debounce, escapeRegExp } from 'lodash'
import apiSearch from 'src/layouts/core-layout/components/CoreSearch/api/apiSearch'
import { ApiSearchResponsePost } from 'src/layouts/core-layout/components/CoreSearch/api/apiSearch.types'
import { MIN_QUERY_LENGTH } from 'src/layouts/core-layout/components/CoreSearch/constants'
import sendYaGoal from 'src/utils/sendYaGoal'
import NavLinks from 'src/layouts/core-layout/components/CoreSearch/components/NavLinks.vue'

const props = defineProps<{
  query: string
  town: {
    id: number
    name: string
    name_pred: string
    translit: string
  }
  isOfficeIp: boolean
  xSpaceClass: string
}>()

const emit = defineEmits(['activate-dropdown-item', 'deactivate-dropdown-item'])

const ICON_MAP: {
  // eslint-disable-next-line no-unused-vars
  [key in NonNullable<ApiSearchResponsePost[number]['results'][number]['icon']>]: string
} = {
  speciality: 'ui-icon-doctor',
  lputype: 'ui-icon-clinic',
  diagnostics: 'ui-icon-layers',
  services: 'ui-icon-layers',
  disease: 'ui-icon-disease',
  town: 'ui-icon-map-pin',
} as const

const root = ref<HTMLDivElement>()

const searchResults = ref<ApiSearchResponsePost>([])
const isLoading = ref(false)
const isFirstRequestDone = ref(false)
const activeItemIndex = ref(0)

const isDropdownItemActivated = ref(false)
const savedQuery = ref('')

const numberOfResults = computed(
  () => searchResults.value.reduce((sum, searchResult) => sum + searchResult.results.length, 0),
)

const searchResultsWithIndexes = computed(() => {
  let index = 1

  return searchResults.value.map(item => ({
    ...item,
    results: item.results.map(resultItem => ({
      ...resultItem,
      index: index++,
    })),
  }))
})

type SearchResultsWithIndexes = typeof searchResultsWithIndexes.value[number]['results'][number]

const activeResultItem = computed<SearchResultsWithIndexes | null>(
  () => {
    let item: SearchResultsWithIndexes | null = null

    searchResultsWithIndexes.value.forEach(
      searchResult => searchResult.results.forEach(
        searchResultItem => {
          if (searchResultItem.index === activeItemIndex.value) {
            item = searchResultItem
          }
        },
      ),
    )

    return item
  },
)

let resultItemDivElements: HTMLDivElement[]

let cancelPreviousRequest = () => {}

const debouncedRequest = debounce(async () => {
  isLoading.value = true

  cancelPreviousRequest()

  let isRequestCancelled = false

  cancelPreviousRequest = () => {
    isRequestCancelled = true
  }

  try {
    const responseData = props.query.length >= MIN_QUERY_LENGTH
      ? (await apiSearch.post({
        query: props.query,
        town: props.town.translit,
      })).data
      : []

    if (!isRequestCancelled) {
      searchResults.value = responseData
    }
  } catch { /* empty */ } finally {
    isLoading.value = false
    isFirstRequestDone.value = true
  }
}, 300)

function scroll(top: number) {
  if (root.value) {
    root.value.parentElement?.scroll({
      top,
      behavior: 'smooth',
    })
  }
}

function deactivateDropdownItem() {
  activeItemIndex.value = 0
  scroll(0)
  emit('deactivate-dropdown-item')
}

watch(() => props.query, () => {
  if (!isDropdownItemActivated.value) {
    debouncedRequest()
    savedQuery.value = props.query
    deactivateDropdownItem()
  }

  isDropdownItemActivated.value = false
}, { immediate: true })

watch(searchResults, () => {
  nextTick(() => {
    resultItemDivElements = root.value
      ? Array.from(root.value.querySelectorAll('.search-results__result-item'))
      : []
  })
})

function highlightQuery(text: string) {
  return text.replace(
    new RegExp(`(${escapeRegExp(savedQuery.value).trim()})`, 'ig'),
    '<span class="text-subtitle-1 primary--text">$1</span>',
  )
}

function handleActivateItem() {
  if (activeItemIndex.value === 0) {
    deactivateDropdownItem()
    return
  }

  isDropdownItemActivated.value = true
  emit('activate-dropdown-item', { title: activeResultItem.value?.title, link: activeResultItem.value?.link })

  const element = resultItemDivElements[activeItemIndex.value - 1]

  if (element) {
    scroll(element.offsetTop - element.offsetHeight)
  }
}

function handleClickSearchResult() {
  if (!props.isOfficeIp) {
    sendYaGoal('oldSearch_dropdownItem')
  }
}

defineExpose({
  moveDown() {
    if (activeItemIndex.value < numberOfResults.value) {
      activeItemIndex.value++
      handleActivateItem()
    }
  },
  moveUp() {
    if (activeItemIndex.value > 0) {
      activeItemIndex.value--
      handleActivateItem()
    }
  },
})
</script>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

.search-results {
  &__image-wrapper {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: $border-radius-md;
  }

  &__image {
    border: 1px solid $ui-kit-bg-gray-60;
    border-radius: $border-radius-md;
    object-fit: contain;
  }

  &__skeletons {
    :deep(.v-skeleton-loader__avatar) {
      width: 16px;
      height: 16px;
    }
  }

  &__result-item {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    text-decoration: none;

    &:hover,
    &_active {
      background-color: $ui-kit-bg-gray-40;
    }
  }
}
</style>
